import React, { useMemo } from 'react'
import { Descendant } from 'slate'

import { IBody } from '@makyoapp/makyo-sdk/models/AppBuilder'
import { ColumnTypes, ITableRow } from '@makyoapp/makyo-sdk/models/Table.js'
import { TypographyGroupValue } from '@makyoapp/makyo-common'
import { CornerRadius, FormContainer, getTextColorStyles } from '@makyoapp/makyo-tw-ui'

import { IViewComponent } from '@/core/app-builder/app/component'
import { IAppLayer } from '@/core/app-builder/app/layer'
import { IDBTable } from '@/core/db/table'
import { CoreComponentInfos, CoreComponentTableData } from '@/core/models/app-components/types'
import { ComponentSettingKey } from '@/shared/builder'
import { parseSpacing } from '@/shared/builder-utils'
import { SpacingInputValue } from '@/shared/builder/types/spacing-input'
import { getSafely } from '@/shared/safely'
import { isEmpty } from '@/shared/utils'

import { CompHeadline } from '../../../shared/CompHeadline'
import { BaseCompFormProps } from './comp'

function filterFormComponentNested(body: IBody[]) {
  let children: IBody[] = []
  body.forEach((el) => {
    if (['form'].includes(el._GroupID)) {
      children.push(el)
    }
    if (el._Body) {
      children.push(...filterFormComponentNested(el._Body))
    }
  })
  return children
}

function getInitialData(
  table: IDBTable,
  component: IViewComponent,
  containerClient: IAppLayer,
  rowId: string | undefined,
  language: string,
  userSpecific: string
) {
  if (!table || !component || !containerClient) return

  // get the children elements and settings
  const childrenElements = filterFormComponentNested(component._Body)
  const containerSettings = containerClient.settings._BodySettings

  if (!containerSettings || isEmpty(childrenElements)) {
    return
  }

  const tableColumns = table.settings._Columns.filter(
    (c) =>
      ![
        ColumnTypes.Slug,
        ColumnTypes.Connection,
        ColumnTypes.Lookup,
        ColumnTypes.Function,
        ColumnTypes.Join,
        ColumnTypes.Math,
        ColumnTypes.Template,
        ColumnTypes.SingleCell,
      ].includes(c._Type)
  )
  const rows = table.GetDbWorkerRows(language, userSpecific)
  const rowData = rows.find((r) => r._id === rowId)
  if (!rowData) return {} as ITableRow

  const filteredData = tableColumns.reduce((acc, col) => {
    acc[col._id] = rowData[col._id]
    return acc
  }, {} as ITableRow)

  return filteredData
}

export interface CompFormWrapperProps {
  infos: CoreComponentInfos
  tableData: CoreComponentTableData
  children: (props: BaseCompFormProps) => React.ReactNode
}

export const CompFormWrapperCore = React.memo(
  ({
    infos: { settings, component, currentParentBg, containerClient, detailTableId, detailRowIndex, rowId },
    tableData: { dbTable: table, language = 'en', userSpecific = '' },
    children,
  }: CompFormWrapperProps) => {
    // ========================= data =========================

    // Initialize default values (details page)
    const initialData = useMemo(() => {
      return getInitialData(table, component, containerClient, rowId, language, userSpecific)
    }, [table, component, containerClient, rowId, language, userSpecific])

    // ========================= design =========================
    // base
    const width = getSafely(settings, [ComponentSettingKey.FormDesignWidth], ['100%'])[0]
    const alignment = settings[ComponentSettingKey.FormDesignHAlign] ?? 'parent'
    const padding = settings[ComponentSettingKey.FormDesignPadding]
    const margin = settings[ComponentSettingKey.FormDesignMargin]

    // form-container
    const innerFieldPadding = parseSpacing(settings?.[ComponentSettingKey.FormDesignFieldMargin] as SpacingInputValue)
    const typoSettings = settings[ComponentSettingKey.FormDesignTypography] as TypographyGroupValue
    const cornerRadius = getSafely(settings, [ComponentSettingKey.FormDesignFieldShape], 'medium') as CornerRadius

    // comp title
    const compTitle = (settings[ComponentSettingKey.FormDataCompTitle] as Descendant[]) ?? []
    const compTitleMargin = settings[ComponentSettingKey.FormDesignCompTitleMargin]
    const compTitleAlign = settings[ComponentSettingKey.FormDesignCompTitleAlign]
    const compTitleTypo = getSafely(typoSettings, [ComponentSettingKey.FormDesignCompTitleTypography], {
      typography: 'headline-small',
    })

    // title color settings
    const compTitleColorType = settings[ComponentSettingKey.FormDesignTitleColor] ?? 'adaptive'
    const compTitleColorSolid = settings[ComponentSettingKey.FormDesignTitleColorSolid]
    const compTitleColorStartGradient = settings[ComponentSettingKey.FormDesignTitleColorStartGradient]
    const compTitleColorEndGradient = settings[ComponentSettingKey.FormDesignTitleColorEndGradient]
    const compTitleColorGradientDirection = settings[ComponentSettingKey.FormDesignTitleColorGradientDirection]

    // label color settings
    const labelColorType = settings[ComponentSettingKey.FormDesignLabelColor] ?? 'adaptive'
    const labelColorSolid = settings[ComponentSettingKey.FormDesignLabelColorSolid]
    const labelColorStartGradient = settings[ComponentSettingKey.FormDesignLabelColorStartGradient]
    const labelColorEndGradient = settings[ComponentSettingKey.FormDesignLabelColorEndGradient]
    const labelColorGradientDirection = settings[ComponentSettingKey.FormDesignLabelColorGradientDirection]

    const labelGradientDirectionCss = (labelColorGradientDirection || 'to-right').replace('-', ' ')

    const labelColorCss = (() => {
      if (labelColorType === 'solid' && labelColorSolid) {
        return `
          .mk-form-container { 
            --field-color-label: var(--color-${labelColorSolid}) !important;
          }
        `
      }
      if (labelColorType === 'gradient' && labelColorStartGradient && labelColorEndGradient) {
        return `
          .mk-form-container { 
            --field-color-label: transparent !important;
          }
          .mk-form-container .mk-form-field label { 
            background-image: linear-gradient(${labelGradientDirectionCss}, var(--color-${labelColorStartGradient}), var(--color-${labelColorEndGradient})) !important;
            -webkit-background-clip: text; 
            background-clip: text; 
            -webkit-text-fill-color: transparent !important; 
          }
        `
      }
      return ''
    })()

    return (
      <FormContainer
        // base
        id={component._id}
        width={width}
        alignment={alignment}
        padding={padding}
        margin={margin}
        backgroundColor={currentParentBg}
        // form
        cornerRadius={cornerRadius}
        className='mk-form-container'
      >
        <style id={`mk-form-container-${component._id}`}>
          {`
          .mk-form-container .mk-form-field:not(.mk-form-submit-button):not(.is-hidden) {
            padding-top: ${innerFieldPadding.paddingTop} !important;
            padding-bottom: ${innerFieldPadding.paddingBottom} !important;
          } 
          ${labelColorCss}
       `}
        </style>

        <CompHeadline
          containerClient={containerClient}
          detailTableId={detailTableId}
          detailRowIndex={detailRowIndex}
          compTitleAlign={compTitleAlign}
          headline={compTitle}
          headlineMargin={compTitleMargin}
          headlineTypo={compTitleTypo}
          backgroundColor={currentParentBg}
          colorType={compTitleColorType}
          styles={getTextColorStyles(
            compTitleColorType,
            compTitleColorSolid,
            compTitleColorStartGradient,
            compTitleColorEndGradient,
            compTitleColorGradientDirection,
            currentParentBg ?? 'primary-90'
          )}
        />

        {children({ initialData })}
      </FormContainer>
    )
  }
)

CompFormWrapperCore.displayName = 'CompFormWrapperCore'
